import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {ContainerSchemaQuery} from 'samples/plate-maps/__generated/ContainerSchemaQuery';
import {
  ADDITIONAL_ATTRIBUTE_TO_DROPDOWN_OPTION,
  DisplayLayerMetadata,
  MeasurementAttribute,
  PlateMapDisplayLayerAttribute,
  PlateMapMeasurementType,
  SupportedAdditionalAttribute,
} from 'samples/plate-maps/PlateMapConstants';
import {CONTAINER_SCHEMA_QUERY} from 'samples/plate-maps/PlateMapQueryUtil';
import {getDisplayLayerAttributeKey, getWellSchemaDisplayLayerOptions} from 'samples/plate-maps/PlateMapUtil';
import {GRAY} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import MenuOptionWithFloatingSubmenu from 'ui-library/menus/MenuOptionWithFloatingSubmenu';
import Option from 'ui-library/menus/Option';
import OptionContainer from 'ui-library/menus/OptionContainer';
import OptionContent from 'ui-library/menus/OptionContent';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';

/**
 * Helper function to check if an attribute is a MeasurementAttribute with a color
 */
function isMeasurementAttribute(attribute: PlateMapDisplayLayerAttribute): attribute is MeasurementAttribute {
  return (
    typeof attribute === 'object' &&
    attribute != null &&
    attribute.hasOwnProperty('color') &&
    typeof (attribute as MeasurementAttribute).color === 'string'
  );
}

/**
 * Get the display name prefix for a measurement type
 */
function getDisplayNamePrefix(measurementType: PlateMapMeasurementType): string {
  switch (measurementType) {
    case PlateMapMeasurementType.TRANSFER_QUANTITY:
      return 'Transfer quantity: ';
    case PlateMapMeasurementType.FINAL_CONCENTRATION:
    case PlateMapMeasurementType.CONTENT_CONCENTRATION:
      return 'Concentration: ';
    default:
      throw new Error(`Unexpected measurement type: ${measurementType}`);
  }
}

/**
 * Renders the display name for measurement/concentration attributes with colored circle indicator
 */
function renderMeasurementDisplayName(
  displayName: string,
  color: string,
  measurementType: PlateMapMeasurementType
): ReactNode {
  const colorStyle = StyleSheet.create({
    coloredCircle: {
      color,
    },
  }).coloredCircle;
  return (
    <span className={css(styles.selectedDisplayContainer)}>
      <BenchlingIcon className={css(styles.selectedOptionIcon)} icon="number" />
      <span>{getDisplayNamePrefix(measurementType)}</span>
      <span className={css(styles.selectedMeasurementContainer)}>
        <BenchlingIcon
          icon="circle"
          className={css(styles.coloredCircle, colorStyle)}
          dataTestAttrs={{component: 'PlateMapDisplayLayerDropdown', element: 'coloredCircle'}}
        />
        <span>{displayName}</span>
      </span>
    </span>
  );
}

function getSelectedDisplayName(
  selectedAttribute: PlateMapDisplayLayerAttribute,
  options: Array<DisplayLayerMetadata>
): ReactNode {
  const selectedAttributeKey = getDisplayLayerAttributeKey(selectedAttribute);
  const selected = options.find(
    (option) => getDisplayLayerAttributeKey(option.attribute) === selectedAttributeKey
  );
  if (selected == null) {
    return '';
  }

  // Show colored circle for measurement attributes, otherwise show icon
  if (isMeasurementAttribute(selected.attribute)) {
    return renderMeasurementDisplayName(
      selected.displayName,
      selected.attribute.color,
      selected.attribute.type
    );
  }

  return (
    <span>
      <BenchlingIcon className={css(styles.selectedOptionIcon)} icon={selected.icon} />
      <span>{selected.displayName}</span>
    </span>
  );
}

interface BasePlateMapDisplayLayerDropdownProps {
  selectedAttribute: PlateMapDisplayLayerAttribute;
  setSelectedAttribute: (attr: PlateMapDisplayLayerAttribute) => void;
  // Which options are available in the dropdown in addition to schema fields
  additionalAttributeOptions: Array<SupportedAdditionalAttribute>;
  plateDataParametersOptions?: Array<DisplayLayerMetadata>;
  measurementLayersOptions?: Array<DisplayLayerMetadata>;
  metadataLayersOptions?: Array<DisplayLayerMetadata>;
}

export type PlateMapDisplayLayerDropdownProps =
  | (BasePlateMapDisplayLayerDropdownProps & {
      containerSchemaId: string | null;
      wellSchemaOptions?: never;
    })
  | (BasePlateMapDisplayLayerDropdownProps & {
      containerSchemaId?: never;
      wellSchemaOptions: Array<DisplayLayerMetadata> | null;
    });

/**
 * Dropdown component for selecting plate map display layers.
 * Display layer options include an option for each schema field on the plate cell's container schema
 * and any additional layers specified such as well role and reagent.
 */
export default function PlateMapDisplayLayerDropdown({
  selectedAttribute,
  setSelectedAttribute,
  additionalAttributeOptions,
  plateDataParametersOptions = [],
  measurementLayersOptions = [],
  metadataLayersOptions = [],
  containerSchemaId = null,
  wellSchemaOptions = null,
}: PlateMapDisplayLayerDropdownProps): JSX.Element {
  const additionalDropdownOptions = additionalAttributeOptions.map(
    (value) => ADDITIONAL_ATTRIBUTE_TO_DROPDOWN_OPTION[value]
  );

  // Separate measurement layers by type
  const concentrationLayers = measurementLayersOptions.filter((layer) => {
    const attr = layer.attribute as MeasurementAttribute;
    return (
      attr.type === PlateMapMeasurementType.FINAL_CONCENTRATION ||
      attr.type === PlateMapMeasurementType.CONTENT_CONCENTRATION
    );
  });

  const transferQuantityLayers = measurementLayersOptions.filter((layer) => {
    const attr = layer.attribute as MeasurementAttribute;
    return attr.type === PlateMapMeasurementType.TRANSFER_QUANTITY;
  });

  function getOptionComponents(options: Array<DisplayLayerMetadata>): Array<ReactNode> {
    return options.map((option) => {
      // For measurement layers, create a dynamic style for the color
      const colorStyle = isMeasurementAttribute(option.attribute)
        ? StyleSheet.create({
            coloredCircle: {
              color: option.attribute.color,
            },
          }).coloredCircle
        : undefined;

      return (
        <Option
          key={getDisplayLayerAttributeKey(option.attribute)}
          value={option.attribute}
          onSelect={() => {
            setSelectedAttribute(option.attribute);
          }}
          icon={option.icon}
          iconClassName={css(styles.optionIcon, colorStyle)}
        >
          <span>{option.displayName}</span>
        </Option>
      );
    });
  }

  const [concentrationOptionRef, setConcentrationOptionRef] = React.useState<HTMLElement | null>(null);
  const [transferQuantityOptionRef, setTransferQuantityOptionRef] = React.useState<HTMLElement | null>(null);

  const concentrationOptionData: OptionData = {
    value: null,
    name: 'Concentration',
    icon: 'number',
    dataTestAttrs: {component: 'PlateMapDisplayLayerDropdown', element: 'concentrationOption'},
  };

  const transferQuantityOptionData: OptionData = {
    value: null,
    name: 'Transfer quantity',
    icon: 'number',
    dataTestAttrs: {component: 'PlateMapDisplayLayerDropdown', element: 'transferQuantityOption'},
  };

  const renderConcentrationOption = (optionData: OptionData): JSX.Element => (
    <OptionContent
      icon={optionData.icon}
      iconClassName={css(styles.optionIcon)}
      hasSubmenu={true}
      dataTestAttrs={optionData.dataTestAttrs}
    >
      <span>{optionData.name}</span>
    </OptionContent>
  );

  const renderTransferQuantityOption = (optionData: OptionData): JSX.Element => (
    <OptionContent
      icon={optionData.icon}
      iconClassName={css(styles.optionIcon)}
      hasSubmenu={true}
      dataTestAttrs={optionData.dataTestAttrs}
    >
      <span>{optionData.name}</span>
    </OptionContent>
  );

  return (
    <BenchlingQuery
      query={CONTAINER_SCHEMA_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
      variables={{storableSchemaId: containerSchemaId}}
      skip={containerSchemaId == null}
    >
      {(containerSchemaData: ContainerSchemaQuery) => {
        const fetchedWellSchemaOptions =
          wellSchemaOptions ?? getWellSchemaDisplayLayerOptions(containerSchemaData);
        return (
          <SelectDropdown
            menu={
              <Menu value={selectedAttribute} width={MenuWidth.FULL} scrollable={true}>
                <Section>
                  {getOptionComponents(additionalDropdownOptions)}
                  {BenchlingConfig.INVENTORY_ENABLE_PLATE_CONCENTRATION_LAYER &&
                    concentrationLayers.length > 0 && (
                      <OptionContainer
                        ref={(node) => {
                          if (node != null) {
                            setConcentrationOptionRef(node.containerRef?.current);
                          }
                        }}
                      >
                        <MenuOptionWithFloatingSubmenu
                          menuOptionContainer={concentrationOptionRef}
                          menuOptionIndex={additionalDropdownOptions.length}
                          optionData={concentrationOptionData}
                          renderOptionContentFn={renderConcentrationOption}
                          submenuProps={{width: MenuWidth.FULL}}
                        >
                          {getOptionComponents(concentrationLayers) as Array<JSX.Element>}
                        </MenuOptionWithFloatingSubmenu>
                      </OptionContainer>
                    )}
                  {BenchlingConfig.INVENTORY_ENABLE_PLATE_CONCENTRATION_LAYER &&
                    transferQuantityLayers.length > 0 && (
                      <OptionContainer
                        ref={(node) => {
                          if (node != null) {
                            setTransferQuantityOptionRef(node.containerRef?.current);
                          }
                        }}
                      >
                        <MenuOptionWithFloatingSubmenu
                          menuOptionContainer={transferQuantityOptionRef}
                          menuOptionIndex={
                            additionalDropdownOptions.length + (concentrationLayers.length > 0 ? 1 : 0)
                          }
                          optionData={transferQuantityOptionData}
                          renderOptionContentFn={renderTransferQuantityOption}
                          submenuProps={{width: MenuWidth.FULL}}
                        >
                          {getOptionComponents(transferQuantityLayers) as Array<JSX.Element>}
                        </MenuOptionWithFloatingSubmenu>
                      </OptionContainer>
                    )}
                </Section>
                {metadataLayersOptions.length > 0 && (
                  <Section>
                    <SectionHeader>Metadata</SectionHeader>
                    {getOptionComponents(metadataLayersOptions)}
                  </Section>
                )}
                <Section>
                  <SectionHeader>Schema Fields</SectionHeader>
                  {getOptionComponents(fetchedWellSchemaOptions)}
                </Section>
                <Section>
                  <SectionHeader>Parameters</SectionHeader>
                  {getOptionComponents(plateDataParametersOptions)}
                </Section>
              </Menu>
            }
            displayName={getSelectedDisplayName(selectedAttribute, [
              ...additionalDropdownOptions,
              ...measurementLayersOptions,
              ...metadataLayersOptions,
              ...fetchedWellSchemaOptions,
              ...plateDataParametersOptions,
            ])}
            alwaysDropDown={true}
            dataTestAttrs={{component: 'PlateMapDisplayLayerDropdown', element: 'root'}}
          />
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  selectedOptionIcon: {
    marginRight: 8,
    color: GRAY,
  },
  optionIcon: {
    margin: '0 8px',
  },
  coloredCircle: {
    marginRight: 6,
  },
  selectedDisplayContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
  },
  selectedMeasurementContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
    padding: '2px 6px',
    borderRadius: 4,
  },
});
